import { DT, quantiseInput, type InputFrame } from "@midnight-pass/sim";

/** Keys that drive the car; their default browser behaviour (page scroll etc.) is suppressed. */
const DRIVING_KEYS = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
]);

/** Keys that fire once per press (not held). */
const EDGE_KEYS = new Set(["KeyR", "Enter", "Backquote", "KeyT", "KeyX"]);

/**
 * Desktop keyboard controls (SPEC §8.1). Keys are digital, so steering ramps towards its target at
 * the car's steering speed to feel analogue. Sampled once per sim tick, so the ramp is tick-based.
 */
export class KeyboardInput {
  private readonly down = new Set<string>();
  private steer = 0;

  constructor(
    private readonly target: Window,
    private readonly onPress: (code: string) => void,
  ) {
    target.addEventListener("keydown", this.handleDown);
    target.addEventListener("keyup", this.handleUp);
    target.addEventListener("blur", this.handleBlur);
  }

  dispose(): void {
    this.target.removeEventListener("keydown", this.handleDown);
    this.target.removeEventListener("keyup", this.handleUp);
    this.target.removeEventListener("blur", this.handleBlur);
  }

  /** Read the keys into an InputFrame. `steeringSpeed` is full-lock units per second. */
  sample(steeringSpeed: number, out: InputFrame): InputFrame {
    const d = this.down;
    const left = d.has("KeyA") || d.has("ArrowLeft");
    const right = d.has("KeyD") || d.has("ArrowRight");
    const target = (right ? 1 : 0) - (left ? 1 : 0);

    // Ramp up at the steering speed; return to centre (or swing across) a bit faster.
    const returning =
      target === 0 || (this.steer !== 0 && Math.sign(target) !== Math.sign(this.steer));
    const rate = steeringSpeed * DT * (returning ? 1.6 : 1);
    const delta = target - this.steer;
    this.steer += Math.max(-rate, Math.min(rate, delta));

    const throttle = d.has("KeyW") || d.has("ArrowUp") ? 1 : 0;
    const brake = d.has("KeyS") || d.has("ArrowDown") ? 1 : 0;
    return quantiseInput(this.steer, throttle, brake, d.has("Space"), out);
  }

  /** Zero the steering ramp (after a reset). */
  centre(): void {
    this.steer = 0;
  }

  private handleDown = (e: KeyboardEvent): void => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (DRIVING_KEYS.has(e.code) || EDGE_KEYS.has(e.code)) e.preventDefault();
    if (e.repeat) return;
    this.down.add(e.code);
    if (EDGE_KEYS.has(e.code)) this.onPress(e.code);
  };

  private handleUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code);
  };

  private handleBlur = (): void => {
    this.down.clear();
  };
}
